import React, { useCallback, useEffect, useState } from "react";
import SearchInput from "@components/input/SearchInput";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import TitleWithSwiper from "@components/swiper/TitleWithSwiper";
import { SwiperSlide } from "swiper/react";
import ProductCard from "@components/card/ProductCard";
import { GET_PRODUCT_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useAxios from "@libs/axios/useAxios";
import TitleNavigateButton from "@components/button/TitleNavigateButton";
import { useTranslation } from "react-i18next";
import NoSearch from "./_components/NoSearch";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.8rem",
});

const BackButton = styled.button({
  display: "flex",
});

const InputWrapper = styled.div({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "2rem",
  padding: "2rem",
});

const ContentWrapper = styled.div({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "4rem",
});

const PaddingContainer = styled.div({
  padding: "0 2rem",
});

const SwiperItem = styled(SwiperSlide)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  color: theme.color.gray,
  fontSize: "1rem",
  lineHeight: "1.2rem",
  width: "fit-content",
  maxWidth: "6rem",
}));

const ImageBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "6rem",
  height: "6rem",
  border: `1px solid ${theme.color.border_gray}`,
  borderRadius: "50%",
  overflow: "hidden",
}));

const Name = styled.div({
  maxWidth: "6rem",
});

const ProductWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1.6rem",
});

const TitleBox = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 2rem",
});

const Title = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "2rem",
  fontWeight: 600,
  margin: 0,
  lineHeight: "2rem",
  display: "flex",
  justifyContent: "space-between",
}));

const ListContainer = styled.div(
  () =>
    ({
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
    }) as const,
);

const SearchDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { axiosInstance } = useAxios();
  const lang = window.localStorage.getItem("language");
  const [inputValue, setInputValue] = useState("");
  const [searchParams] = useSearchParams();
  const searchParam = searchParams.get("query");
  const { getSearchBrandList, getSearchSellerList } = useApiRequest();
  const { t } = useTranslation();

  useEffect(() => {
    if (searchParam) {
      setInputValue(searchParam);
    }
  }, []);

  const { data: searchBrandList } = useQuery({
    queryKey: ["searchBrandList", inputValue],
    queryFn: () => getSearchBrandList(inputValue),
    enabled: !!inputValue || inputValue !== "",
  });

  const { data: searchSellerList } = useQuery({
    queryKey: ["searchSellerList", inputValue],
    queryFn: () => getSearchSellerList(inputValue),
    enabled: !!inputValue || inputValue !== "",
  });

  const getSearchProductList = useCallback(
    async (inputValue: string) => {
      let url = `${GET_PRODUCT_URL}?page=1&limit=${LOAD_SIZE_10}&search_value=${inputValue}`;

      const res = await axiosInstance.get(url);
      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance],
  );

  const { data: searchProductList } = useQuery({
    queryKey: ["searchProductList", inputValue],
    queryFn: () => getSearchProductList(inputValue),
    enabled: !!inputValue || inputValue !== "",
  });

  const handleBack = () => {
    navigate(`/search`, { replace: true });
  };

  return (
    <Container>
      <InputWrapper>
        <BackButton onClick={handleBack}>
          <Image src={theme.image.arrow_left.default} />
        </BackButton>
        <SearchInput
          placeholder="아이템, 셀러 브랜드 명, 키워드 검색"
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      </InputWrapper>

      {searchBrandList?.length > 0 ||
      searchSellerList?.length > 0 ||
      searchProductList?.data?.list?.length > 0 ? (
        <ContentWrapper>
          {searchBrandList?.length > 0 && (
            <PaddingContainer>
              <TitleWithSwiper
                title="브랜드"
                route={`/search/brand?query=${inputValue}`}
                preview="auto">
                {searchBrandList?.map((item: any, index: number) => (
                  <SwiperItem
                    key={index}
                    onClick={() => navigate(`/brand/${item.brand_id}`)}>
                    <ImageBox>
                      <Image key={index} src={item.logo_url} size="100%" />
                    </ImageBox>
                    <Name className="ellipsis-1">
                      {lang === "en" ? item.name_en : item.name_ko}
                    </Name>
                  </SwiperItem>
                ))}
              </TitleWithSwiper>
            </PaddingContainer>
          )}

          {searchSellerList?.length > 0 && (
            <PaddingContainer>
              <TitleWithSwiper
                title="셀러"
                route={`/search/seller?query=${inputValue}`}
                preview="auto">
                {searchSellerList?.map((item: any, index: number) => (
                  <SwiperItem
                    key={index}
                    onClick={() => navigate(`/seller/${item.seller_id}`)}>
                    <ImageBox>
                      <Image
                        key={index}
                        src={
                          item.profile_image
                            ? item.profile_image
                            : theme.image.default_user.default
                        }
                        size="100%"
                      />
                    </ImageBox>
                    <Name className="ellipsis-1">{item.nickname}</Name>
                  </SwiperItem>
                ))}
              </TitleWithSwiper>
            </PaddingContainer>
          )}

          {searchProductList?.data?.list?.length > 0 && (
            <ProductWrapper>
              <TitleBox>
                <Title>{t("상품")}</Title>
                <TitleNavigateButton
                  title="전체보기"
                  route={`/search/product?query=${inputValue}`}
                />
              </TitleBox>
              <ListContainer>
                {searchProductList?.data?.list?.map((product: any) => (
                  <ProductCard
                    key={product.product_id}
                    id={product.product_id}
                    brand={
                      lang === "en"
                        ? product.brand_name_en
                        : product.brand_name_ko
                    }
                    name={
                      product.product_name_en && lang === "en"
                        ? product.product_name_en
                        : product.product_name_ko
                    }
                    src={product.thumbnail_url}
                    price={product.price}
                    likeCount={product.like_total}
                    chatCount={product.qna_total}
                    haveLike
                    soldout={product.is_sold_out === 1}
                    isLiked={product.is_liked}
                    condition={product.product_condition}
                  />
                ))}
              </ListContainer>
            </ProductWrapper>
          )}
        </ContentWrapper>
      ) : (
        <NoSearch />
      )}
    </Container>
  );
};

export default SearchDetail;
